import { Translation } from '.';
import { Event, PaidVia, PaymentStatus } from '../documents/restaurants/event';
import { Relationship } from './guests';
import { Message } from './message';
import { Attachment } from './shifts';
import {
  CustomElementType,
  CustomElementMode,
  CustomElementRule,
  CustomDataValue,
  CustomElement,
  CustomElementFormular,
} from './customElements';
import { type } from 'io-ts';
import { SetOnboardingActionRequest } from '../routes/v03/onboarding';
import { AdvancedOccassion, OptionList, Policy } from './occassion';
import { CartItem } from '../documents/restaurants/addOnOrder';
import { AddOn } from '../documents/restaurants/addOn';

export { OccassionType } from './occassion';

export type Occassion = AdvancedOccassion;

export type VoucherExperience = unknown;

export enum ReservationStatus {
  SUCCESS = 'success',
  FAILED = 'failed',
  POSITIVE = 'positive',
  PENDING = 'pending',
  NEGATIVE = 'negative',
}

export type ReservationCard = {
  id: string;
  time: string;
  date: string;
  customId: string;
  restaurantId: string;
  occassion: string;
  status: ReservationStatus;
  guests: number;
  editable: boolean;
  confirmed: boolean;
  validTill: number;
  source: string;
  feedbackRating: number;
  done: boolean;
  ticketId: string;
  guest: {
    name: string;
    phone: string;
    comment: string;
    email: string;
    uid: string;
  };
  restaurant: {
    id: string;
    name: string;
    img: string;
    occassions: {
      id: string;
      title: string;
    }[];
  };
};

export type AvailableTime =
  | {
      time: null | string;
      block: null | number;
      combinations: {
        expression: string;
        tables: string[];
        priority: number;
        reservationLength: number;
        space: string;
        ispreferredSpace: boolean;
      }[];
      tables: string[];
      reservationLength: null | number;
      shiftPriority: number;
      maxGuestsReached: boolean;
      blockedSlots: number;
      totalSlots: number;
      shiftId: string;
      placeAt: null | 'indoor' | 'outdoor';
      closingAt: number | string;
      type: 'regular' | 'light';
    }
  | {
      time: string;
      block: number;
      reservationLength: number;
      tables: null | string[];
      space: string | null;
      expression: string;
      combinations?: {
        expression: string;
        tables: string[];
        priority: number;
        reservationLength: number;
        space: string;
        ispreferredSpace: boolean;
      }[];
      type: 'regular' | 'light';
    };

export type ReservationSnippet = {
  guestId: string | null;
  guests: number;
  status: ReservationStatus;
  occassion: string;
  time: string;
  space: string | null;
  tables: string[] | null;
  tableStr: string | null;
};

export interface SummedUpReservation {
  g: number; //guests
  t: string; //tables "arr[0];arr[1];..."
  s: number; //start
  e: number; //end
  sId?: number; //ShiftId
  o: number; //Occassion
  sp: number; //Spaces
  st: number; //Status
  c?: number; //createdAt / 60000 and rounded
  attr: string; //guest.attr "0;1;2"
  eFs: number; // 0 or 1 excludeFromSlots
}

export type View = any;

export type Decoration = any;

export type Shift = any;

export type Wall = any;

export interface OldSpace {
  id: string;
  title: string;
  size: {
    height: number;
    width: number;
  };
  maxCaps?: number;
}

export interface Space extends Omit<OldSpace, 'title'> {
  name: string;
  description: string;
  offset: { x: number; y: number };
  outdoor: boolean;
}
export interface Table {
  id: string;
  name: string;
  space: string;
  expression: string | false | null;
  min: number;
  max: number;
  occassions: string[];
  priority: number;
  standAlone?: boolean;
  type: string;
  coords: {
    x: number;
    y: number;
    size: number;
    direction: number;
  };
  attributes?: string[];
  alias?: string;
  windowTable: boolean;
  /**
   * Shared-seating flag. When true, this physical table can host multiple
   * reservations concurrently as long as the sum of their guests ≤ max.
   * Opt-in per table; defaults to false. See plan: distributed-enchanting-stroustrup.md
   */
  shared?: boolean;
}

export interface TableCombination {
  id: string;
  primaryString: string;
  secondaryString: string;
  space: string;
  expression: string | false | null;
  min: number;
  max: number;
  occassions: string[];
  tables: string[];
  priority: number;
  attributes?: string[];
  /**
   * Derived: true iff every member table has `shared: true`.
   * Recomputed by checkTableCombinations whenever membership changes.
   * Mixed (shared + non-shared) combinations are rejected by the editor.
   */
  shared?: boolean;
}

export type Background = {
  spaceId: string;
  floorPlanId?: string;
  imageUrl: string;
  width: number;
  height: number;
  factor: number; // defaults to 10
  offset?: {
    x: number;
    y: number;
  };
};

export type Backgrounds = Record<string, Background>;
export interface FloorPlan {
  id?: string;
  name: string;
  description: string;
  icon: null | string;
  active: boolean;
  standard: boolean;
  sameDayMinGuestsTwo?: boolean;
  spaces: Space[];
  tableCombinations: TableCombination[];
  tables: Table[];
  decorations: Decoration[];
  walls: Wall[];
  updatedAt: number;
  publishedAt: number;
  backgrounds?: Backgrounds;
}

export enum OptionsType {
  WEBSITE = 'WEBSITE',
  WAITER = 'WAITER',
  PHONE = 'PHONE',
}

export type Options = {
  type: OptionsType;
  uid: string;
  id: null | string;
  resLength: null | number;
  time: null | string;
  preferredSpace: null | string;
  currentTime: null | string;
  guests: number;
  attr: string[];
  occassion: string;
  reservationType: 'regular' | 'light';
  maxGuestsOnWebsite: number;
  hasWaitingList: boolean;
  minTimeInAdvance?: number;
  prohibitErrors?: boolean;
};

export interface ReservationGuest
  extends Omit<Relationship, 'restaurantId' | 'guestId'> {
  comment?: string;
  categories?: string[];
  faqAnswers?: {
    id: string;
    answer: string;
    translations?: { language: string; data: { answer: string } }[];
  }[];
  sendEmail?: boolean;
  sendSms?: boolean;
  confirmedDetails?: boolean;
  creditCardCustomerId?: string;
  guestId?: string;
}

export type Reservation = {
  id?: string;
  restaurant: string;
  versionChange?: boolean;
  status: ReservationStatus;
  date: string;
  time: string;
  space?: string | null;
  occassion: string;
  tables: string[] | null;
  tableStr?: string | null;
  customId?: string;
  source?: string;
  startTimeInBlocks?: number;
  started?: number | null;
  isTest?: boolean;
  done?: true | null;
  endTimeInBlocks?: number;
  reservationLength: number;
  feedbackRating?: number;
  guest: ReservationGuest;
  confirmationEmailId?: string;
  guests: number;
  noShow?: boolean | null;
  noShowFee?: {
    amount: number;
    description: string;
    ts: number;
    charge: string;
  };
  noShowConfirmation?: {
    validTill: number;
    sendVia: 'sms' | 'email';
  };
  blocked?: boolean | null;
  oldReservationLength?: string;
  blockedFullShift?: boolean;
  ticketId?: null | string;
  event?: Event | null;
  emails?: {
    confirmationEmail?: boolean;
    reminderEmail?: boolean;
    feedBackEmail?: boolean;
  };
  fixed?: boolean;
  eventId?: null | string;
  sendCancelationEmail?: boolean;
  emailStatus?: string;
  emailErrorMessage?: string;
  validTill?: number;
  createdAt?: number;
  createdBy?: string;
  updatedAt?: number;
  updateNote?: {
    note: string;
    updatedAt?: number;
  };
  updatedBy?: string;
  excludeFromSlot?: boolean;
  walkIn?: boolean;
  custom?: Record<string, unknown>;
  confirmedVia?: string;
  confirmedBy?: string;
  canceledBy?: string;
  canceledVia?: string;
  excludeFromSlots?: boolean;
  cancelationReason?: string;
  subscriptionUpdated?: boolean;
  additional?: Record<string, any>;
  automaticallySetToDone?: boolean;
  warnings?: (
    | {
        id?: string;
        message: string;
      }
    | string
  )[];
  sendConfirmationEmail?: boolean;

  fromWaitinglist?: boolean;

  stammtischId?: string;

  minimumConsumption?: {
    id: string;
    title: Record<string, string>;
    description: Record<string, string>;
    amount: number;
    paid: boolean;
    status: 'missing_payments' | 'processing' | 'failed' | 'succedded';
  };
  timeStamps?: {
    seatedAt: number;
    system: string;
    firstOrderAt?: number;
    lastOrderAt?: number;
    paidAt?: number;
    leftAt?: number;
  };
  paidAmount?: number;
  idempotency_token?: string;
  addedInformations?: {
    attachments?: Attachment & { internalUseOnly?: boolean }[];
  };
  customData?: Record<string, CustomDataValue>;
  automaticCancelationReason?: string;
  /**
   * Machine-readable reason the reservation ended up in a cancelled/failed state.
   * Used by update-handler email guards (skip cancellation email for
   * `'payment_abandoned'`) and by next-dashboard to show a distinct label
   * ("Abgebrochen") instead of "Storniert".
   */
  cancelationType?: 'guest' | 'staff' | 'payment_abandoned' | 'no_show' | 'auto' | null;
  /**
   * Phase A — reservation is in a time-bounded holding state defined by
   * `validTill`. EVERY reservation starts preliminary to block the slot while
   * the form is being filled out (≈5-min hold); the flag flips off (or the
   * field is dropped) once the reservation is final:
   *   - Non-ticket reservations: confirmed on form submission.
   *   - Ticket / payment-bound reservations: stay preliminary with an
   *     EXTENDED `validTill` until the payment completes. Guest name, email,
   *     phone are populated during this stage but the reservation is still
   *     disposable if the payment never arrives (validTill expires → slot
   *     freed, supersedable by any other booking).
   *
   * Phase A starts writing this flag explicitly. Today
   * `isPreliminaryReservation()` relies on the guest name substring
   * (`"Wird gerade erstellt..."`), which only catches the form-filling stage
   * and misses the payment-wait stage. The explicit flag captures both. The
   * name check stays as a fallback for pre-flag historical data.
   */
  preliminary?: boolean;
  /**
   * Phase A — which preliminary stage the reservation is in:
   *   - `'form'`        initial 5-min hold while the guest fills the form.
   *   - `'payment'`     guest data submitted, awaiting ticket / voucher
   *                     payment with an extended `validTill`.
   *   - `'waitinglist'` slot held via waitinglist conversion.
   */
  preliminaryReason?: 'form' | 'payment' | 'waitinglist';
  /**
   * Set when a late ticket payment arrives for a reservation that was previously
   * marked FAILED, but no free table is available at the original slot (e.g.
   * another reservation has since been seated there). The reservation is
   * resurrected with `tables: []` and this flag, so staff sees a warning banner
   * in next-dashboard and can place the guest manually instead of silently
   * double-booking.
   */
  paymentArrivedButNoSeat?: boolean;
  /**
   * What the guest gets back after the restaurant cancelled the ticket from the
   * dashboard. Read by the cancellation email. The voucher code lives here and
   * not on the ticket, because tickets are readable by every signed-in user.
   */
  ticketRefund?: {
    method: 'mollie' | 'voucher' | 'originalVoucher' | 'none';
    status?: 'pending' | 'refunded' | 'failed' | 'partial' | 'unknown';
    emailStatus?: 'pending' | 'sent' | 'failed' | 'manual';
    documentNumber?: string | null;
    lateRefundAmount?: number;
    lateRefundPendingAmount?: number;
    amount: number;
    voucherCode?: string | null;
    /** Where the voucher was emailed to; null means staff hands it over. */
    voucherEmailedTo?: string | null;
  } | null;
  /**
   * Ticket-Plan Phase 1 — explicit pending-lifecycle state of a
   * payment-bound reservation. The sole writer is `ticketLifecycle.ts`
   * (behind the `ticketLifecycleV2` flag): crons flip holds to `'expired'`
   * instead of hard-deleting them. Absent for non-ticket reservations.
   */
  paymentState?: 'pending' | 'paid' | 'expired' | 'cancelled';

  cancelTill?: {
    ts: number;

    data: {
      // sms?: {
      //   [lang: string]: string;
      // };
      // email?: {
      //   [lang: string]: string;
      // };
      cancelButton?: {
        [lang: string]: string;
      };
      editButton?: {
        [lang: string]: string;
      };
      reservationDisclaimer?: {
        [lang: string]: string;
      };
      internalNoteOnCancel?: {
        message: string;
        email?: string; // recepients
      };
      internalNoteOnEdit?: {
        message: string;
        email?: string; // recepients
      };
      forbidCancel?: boolean;
      forbidEditGuests?: boolean;
      forbidEditTime?: boolean;
      forbidEditDate?: boolean;
      forbidEditOccassion?: boolean;
    };
  } | null;

  partlyPlaced?: boolean;

  experienceVoucher?: {
    id: string;
    occassion: string | null;
    guests: number | null;
    status: string;
    voucher: {
      voucher_id: string;
      title: string;
      text: string;
      image: string | null;
      addOns: ({
        id: string;
        image: string | null;
        title: string;
        description: string;
        options: string[];
      } | null)[];
    };
  } | null;
};

export enum RequestStatus {
  PENDING = 'pending', // default
  WAITING = 'waiting', // waiting for response from guest
  SUCCESS = 'success', // successful reservation
  FAILED = 'failed', // guest did not respond in time
  CANCELED = 'canceled', // canceled by guest or restaurant
}

export type Request = {
  id: string;
  restaurant: string; // restaurantId
  date: string; // 2023-12-31

  time: string; // 18:00
  flexibleTime?: null | {
    earliest: string;
    latest: string;
  };

  occassion: string; // occassionId
  customId?: string; // MOND
  guest: Reservation['guest'];
  guests: number;

  customData?: Reservation['customData']; // not used at this stage
  confirmTill?: number; // not used at this stage
  confirmedVia?: string; // not used at this stage
  confirmedBy?: string; // not used at this stage
  confirmNote?: string; // not used at this stage

  preferredFormOfContact?: 'call' | 'whatsapp';

  type?: 'waitinglist' | 'largeGroup';

  canceledVia?: string;
  canceledBy?: string;
  cancelNote?: string;
  canceledAt?: number;

  status: RequestStatus; // default is pending

  createdAt?: number;
  createdBy?: string; // when made f

  source: string;

  updatedAt?: number;
  updatedBy?: string;
};

export interface WaitingListEntry extends Request {
  type: 'waitinglist';
}

export interface LargeGroup extends Request {
  type: 'largeGroup';
}

export type {
  CustomElementType,
  CustomElementMode,
  CustomElementRule,
  CustomDataValue,
  CustomElement,
  CustomElementFormular,
};

export type DayOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6; // 0: Sunday, 1: Monday, ..., 6: Saturday

export interface DailyRule {
  type: 'daily';
  every: number;
  offset?: number;
}

export interface SpecificRule {
  type: 'specific';
  dates: string[];
}

export interface WeeklyRule {
  type: 'weekly';
  days: DayOfWeek[]; // Array of days
  every: number;
  offset?: 0 | 1;
}

export interface MonthlyDayOfWeekRule {
  type: 'monthlyDayOfWeek';
  option: number;
  days: DayOfWeek[]; // Array of days
}

export interface MonthlySpecificDayRule {
  type: 'monthlySpecificDay';
  day: number;
}

export interface YearlySpecificDateRule {
  type: 'yearlySpecificDate';
  date: string; // in 'dd.mm' format
}

export type ReservationRule =
  | DailyRule
  | WeeklyRule
  | MonthlyDayOfWeekRule
  | MonthlySpecificDayRule
  | YearlySpecificDateRule
  | SpecificRule;

export interface Stammtisch {
  id: string;
  active: boolean;
  restaurant: string;
  reservation: Partial<Reservation> & { guest: ReservationGuest };
  rule: ReservationRule;
  endDate?: string;
}

export interface Ticket {
  id: string;

  restaurantId: string;

  voucherId: string;

  reservationId: string;

  optionLists: OptionList[];
  cart: CartItem[];

  payments: {
    id?: string;
    amount: number;
    paidVia: PaidVia;
    paymentId?: string;
    createdAt?: number;
    status: PaymentStatus;
    paymentLink: string;
    testmode: boolean;
  }[];

  addOns?: {
    addOns: AddOn[];
    cart: CartItem[];
  };

  invoiceLink?: string;

  valid: boolean;

  total: number;
  vat: number;
  serviceFee: number;
  payAtRestaurant?: boolean;

  company: {
    name: string;
    address: string;
    postalCode: string;
    city: string;
    country: string;
  } | null;

  createdAt?: number;

  canceledAt?: number;
  canceledBy?: string;

  status:
    | 'missing_payments'
    | 'processing'
    | 'failed'
    | 'succedded'
    | 'refunded';
}

export interface AdvancedReservation extends Reservation {
  // Policies
  policies?: Policy[]; // @TODO

  // Inventories
  usedInventories?: {
    id: string;
    count: number;
    connectedTo: string; // op_optionListId_optionId | ad_addOnId_optionId
  }[];

  // FloorPlan
  ignoreFloorPlan?: boolean;

  // Fees
  fee?: unknown; // @TODO

  // Min Consumption
  minConsumption?: unknown; // @TODO

  // Hold
  hold?: {
    mandateId: string | null;
    customerId: string | null;
    details?: {
      method: 'paypal' | 'creditcard' | 'sepa';
      consumerName?: string | null;
      consumerAccount?: string | null;
      bankAccount?: string | null;
      bankName?: string | null;
      cardHolder?: string | null;
      cardNumber?: string | null;
      cardLabel?: string | null;
    };
    // Check Payments
  };
  // Add Ons

  // Vouchers
  voucherCode?: string;
  voucherId?: string;

  // Ticket
  ticketV02Id?: string; // @TODO
  ticket?: Ticket;

  // Payments
  payments?: unknown; // @TODO

  // type
  type?: 'reservation' | 'course';
}
